<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  currentFilters: { type: Array, default: () => [] },
});

const emit = defineEmits(['loadSegment']);

const { t } = useI18n();
const store = useStore();

const segments = useMapGetter('campaignSegments/getSegments');

const isSaveDialogOpen = ref(false);
const segmentName = ref('');
const isSaving = ref(false);

onMounted(() => {
  store.dispatch('campaignSegments/fetch');
});

const hasFilters = computed(
  () =>
    props.currentFilters.length > 0 &&
    !(
      props.currentFilters.length === 1 && !props.currentFilters[0].attributeKey
    )
);

const saveSegment = async () => {
  if (!segmentName.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('campaignSegments/create', {
      name: segmentName.value.trim(),
      filters: { payload: props.currentFilters },
    });
    segmentName.value = '';
    isSaveDialogOpen.value = false;
  } catch {
    // Error handled by store
  } finally {
    isSaving.value = false;
  }
};

const loadSegment = segment => {
  const filters = segment.filters?.payload || [];
  emit('loadSegment', filters);
};

const deleteSegment = async id => {
  await store.dispatch('campaignSegments/delete', id);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Saved segments list -->
    <div v-if="segments.length > 0" class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('CAMPAIGN.SEGMENTS.LOAD_LABEL') || 'Saved Segments' }}
      </label>
      <div class="flex flex-wrap gap-1">
        <div
          v-for="segment in segments"
          :key="segment.id"
          class="flex items-center gap-1 px-2 py-1 text-xs rounded bg-n-alpha-2 hover:bg-n-alpha-3"
        >
          <button
            class="text-n-slate-12 hover:text-n-blue-11"
            @click="loadSegment(segment)"
          >
            {{ segment.name }}
          </button>
          <button
            class="text-n-slate-10 hover:text-n-ruby-11"
            @click="deleteSegment(segment.id)"
          >
            <i class="i-ph-x !text-[10px]" />
          </button>
        </div>
      </div>
    </div>

    <!-- Save as segment -->
    <div v-if="!isSaveDialogOpen">
      <Button
        v-if="hasFilters"
        sm
        ghost
        slate
        class="text-xs"
        @click="isSaveDialogOpen = true"
      >
        <i class="i-ph-floppy-disk !text-xs mr-1" />
        {{ t('CAMPAIGN.SEGMENTS.SAVE_BUTTON') || 'Save as segment' }}
      </Button>
    </div>

    <div v-else class="flex items-end gap-2">
      <Input
        v-model="segmentName"
        :placeholder="t('CAMPAIGN.SEGMENTS.NAME_PLACEHOLDER') || 'Segment name'"
        class="flex-1"
        message-type="info"
      />
      <Button
        sm
        solid
        blue
        :is-loading="isSaving"
        :disabled="!segmentName.trim() || isSaving"
        @click="saveSegment"
      >
        {{ t('CAMPAIGN.SEGMENTS.SAVE_CONFIRM') || 'Save' }}
      </Button>
      <Button sm faded slate @click="isSaveDialogOpen = false">
        {{ t('CAMPAIGN.SEGMENTS.CANCEL') || 'Cancel' }}
      </Button>
    </div>
  </div>
</template>
